/**
 * Posle OIDC /login odgovora: automatski puni UserPreferencesService (uloga, kontekst org/inst).
 * Koristi istu semantiku id/name kao useUserProfile (handleSavePreferences).
 */
import UserPreferencesService from '@/shared/services/UserPreferencesService';
import CSOProfileService from '@/shared/services/cso/CSOProfileService';
import PublicBodyService from '@/shared/services/cso/PublicBodyService';
import {
  splitOrganizationsByType,
  buildApprovedEntityIdSet,
  isSelectableMembership,
} from '@/features/user/profile/profileUtils';
import { isPlatformAdmin, normalizePlatformRoles } from '@/shared/auth/obligationAuth';

const HOME_PATH = '/';
const PROFILE_SETTINGS_PATH = '/profil?tab=settings';

/** Izvlači CSO organization id iz različitih BE shape-ova. */
function getCsoId(org) {
  if (org == null) return null;
  const raw =
    org.organization_id ??
    org.organizationId ??
    org.cso_id ??
    org.org_id ??
    org.organization?.id ??
    org.cso?.id ??
    org.id ??
    org;
  if (raw == null) return null;
  if (typeof raw === 'object') return raw.id ?? raw.organization_id ?? raw.organizationId ?? null;
  return raw;
}

/** Izvlači prikazno ime CSO organizacije. */
function getCsoName(org) {
  if (org == null) return null;
  const n =
    org.organization_name ??
    org.organizationName ??
    org.organization?.name ??
    org.cso?.name ??
    org.name ??
    org.naziv;
  return typeof n === 'string' && n.trim() ? n.trim() : null;
}

/** Izvlači institution/public body id iz BE zapisa. */
function getPbId(inst) {
  if (inst == null) return null;
  const raw = inst.organization_id ?? inst.institution_id ?? inst.institutionId ?? inst.id ?? inst;
  return raw != null ? raw : null;
}

/** Izvlači prikazno ime institucije (public body). */
function getPbName(inst) {
  if (inst == null) return null;
  const n =
    inst.organization_name ??
    inst.institution_name ??
    inst.institutionName ??
    inst.name ??
    inst.naziv;
  return typeof n === 'string' && n.trim() ? n.trim() : null;
}

/** Jedinstveni zapis po string id-ju. */
function uniqueEntities(items, getId) {
  const byKey = new Map();
  for (const item of items) {
    const id = getId(item);
    if (id == null) continue;
    const key = String(id);
    if (!byKey.has(key)) byKey.set(key, item);
  }
  return [...byKey.values()];
}

/** Samo verified članstva čiji je entitet admin-odobren (selectable). */
function filterSelectableEntities(items, membershipList, approvedIds, getId) {
  return uniqueEntities(
    (items || []).filter((item) => isSelectableMembership(getId(item), membershipList, approvedIds)),
    getId,
  );
}

/**
 * @param {object} userData – Korisnik sa `loginData.roles` i `loginData.organizations`.
 * @returns {Promise<{ targetPath: string }>} Ciljna ruta nakon automatskog punjenja preferenci.
 */
export async function applyPostLoginPreferences(userData) {
  const hasAdmin = isPlatformAdmin(userData);

  const prefs = UserPreferencesService.getPreferences();

  if (hasAdmin) {
    const adminRoles = normalizePlatformRoles(userData).filter((r) =>
      ['ADMIN', 'OGP_ADMIN', 'EVALUATOR'].includes(r)
    );
    UserPreferencesService.savePreferences({
      ...prefs,
      selectedRole: adminRoles[0] || 'ADMIN',
    });
    return { targetPath: HOME_PATH };
  }

  const rawOrgs = userData?.loginData?.organizations;
  const split = splitOrganizationsByType(rawOrgs);

  const [approvedCsos, approvedPublicBodies] = await Promise.all([
    CSOProfileService.getAllOrganizations().catch(() => []),
    PublicBodyService.getAllPublicBodies().catch(() => []),
  ]);
  const approvedOrgIds = buildApprovedEntityIdSet(approvedCsos);
  const approvedInstIds = buildApprovedEntityIdSet(approvedPublicBodies);

  const csos = filterSelectableEntities(
    split.organizations.verified,
    split.organizations,
    approvedOrgIds,
    getCsoId,
  );
  const pbs = filterSelectableEntities(
    split.institutions.verified,
    split.institutions,
    approvedInstIds,
    getPbId,
  );

  if (csos.length === 1 && pbs.length === 0) {
    const org = csos[0];
    const id = getCsoId(org);
    UserPreferencesService.savePreferences({
      ...prefs,
      selectedRole: 'CITIZEN',
      userType: 'organization',
      selectedOrganizationId: id != null ? String(id) : null,
      selectedOrganizationName: getCsoName(org) || 'Организација',
      selectedInstitutionId: null,
      selectedInstitutionName: null,
    });
    return { targetPath: HOME_PATH };
  }

  if (pbs.length === 1 && csos.length === 0) {
    const inst = pbs[0];
    const id = getPbId(inst);
    UserPreferencesService.savePreferences({
      ...prefs,
      selectedRole: 'CITIZEN',
      userType: 'institution',
      selectedOrganizationId: null,
      selectedOrganizationName: null,
      selectedInstitutionId: id != null ? String(id) : null,
      selectedInstitutionName: getPbName(inst) || 'Институција',
    });
    return { targetPath: HOME_PATH };
  }

  UserPreferencesService.savePreferences({
    ...prefs,
    selectedRole: 'CITIZEN',
    userType: 'individual',
    selectedOrganizationId: null,
    selectedOrganizationName: null,
    selectedInstitutionId: null,
    selectedInstitutionName: null,
  });
  return { targetPath: PROFILE_SETTINGS_PATH };
}
